import React, { useState } from 'react';
import { Button, Card, Badge } from './components';
import { Building2, Globe, TrendingUp, AlertCircle, Target, Download, ArrowLeft, ArrowRight } from 'lucide-react';
import SompoRoleplay from './SompoRoleplay';

const SompoDemo: React.FC = () => {
  const [showRoleplay, setShowRoleplay] = useState(false);

  if (showRoleplay) {
    return <SompoRoleplay onBack={() => setShowRoleplay(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#fe4a0b] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Sompo Insurance APAC</h1>
            </div>
            <Badge className="bg-[#fe4a0b]/10 text-[#fe4a0b] border-[#fe4a0b]/20">
              Commercial Distribution
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Meeting Context */}
        <Card className="mb-8 border-0 shadow-lg">
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Pre-Meeting Intelligence</h2>
                <p className="text-gray-600">Marine Cargo Transportation • Meeting in 2 hours</p>
              </div>
              <Badge variant="destructive" className="bg-red-100 text-red-700 border-0">
                high impact
              </Badge>
            </div>

            {/* Client Profile */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-5 h-5 text-[#fe4a0b]" />
                <h3 className="text-lg font-semibold text-gray-900">Client Profile</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900">Pacific Shipping Co.</h4>
                  <p className="text-gray-600">Marine Cargo Transportation</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Current Coverage</p>
                    <p className="text-xl font-bold text-gray-900">$50M Marine Liability</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Renewal</p>
                    <p className="text-xl font-bold text-gray-900">March 2025</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h5 className="font-medium text-gray-900 mb-3">Key Metrics:</h5>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Fleet Size</p>
                      <p className="font-semibold text-gray-900">24 vessels</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Annual Revenue</p>
                      <p className="font-semibold text-gray-900">$125M</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Claims Ratio</p>
                      <p className="font-semibold text-gray-900">2.3%</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <h5 className="font-medium text-gray-900 mb-2">Recent Changes:</h5>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full" />
                      Expanded fleet by 3 vessels
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full" />
                      New routes to Southeast Asia
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#fe4a0b] rounded-full" />
                      Upgraded cargo tracking systems
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Market News */}
            <div className="bg-orange-50 rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-[#fe4a0b]" />
                  <h3 className="text-lg font-semibold text-gray-900">Market News</h3>
                </div>
                <Badge variant="destructive" className="bg-red-100 text-red-700 border-0">
                  high impact
                </Badge>
              </div>

              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  New Suez Canal Tariffs Increase by 15% Starting Q1 2025
                </h4>
                <p className="text-sm text-gray-600 mb-3">Maritime Executive • 2 hours ago</p>

                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Client Impact:</span> Higher shipping costs will drive demand for cargo delay insurance
                    and increased coverage limits for route diversification risks.
                  </p>
                </div>
              </div>
            </div>

            {/* Opportunity */}
            <div className="bg-green-50 rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Opportunity</h3>
                </div>
                <Badge className="bg-green-100 text-green-700 border-0">
                  high impact
                </Badge>
              </div>

              <h4 className="font-semibold text-gray-900 mb-3">Expanded Marine Cargo Coverage</h4>
              <p className="text-gray-600 mb-4">
                With rising canal tariffs, clients need enhanced coverage for alternative routing and potential delays.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-500 mb-1">Potential Value</p>
                  <p className="font-semibold text-gray-900">$2.5M premium increase</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-sm text-gray-500 mb-1">Timeline</p>
                  <p className="font-semibold text-gray-900">Q1 2025</p>
                </div>
              </div>
            </div>

            {/* Meeting Strategy */}
            <div className="bg-blue-50 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Meeting Strategy</h3>
                </div>
                <Badge className="bg-blue-100 text-blue-700 border-0">
                  high impact
                </Badge>
              </div>

              <h4 className="font-medium text-gray-900 mb-3">Recommended Talking Points</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-[#fe4a0b] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Position cargo delay insurance as essential given new tariff structure</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-[#fe4a0b] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Highlight competitor losses from similar route disruptions</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-[#fe4a0b] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Propose fleet expansion coverage for their 3 new vessels</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-[#fe4a0b] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Discuss cyber coverage for upgraded tracking systems</span>
                </li>
              </ul>

              <div className="flex gap-3">
                <Button
                  onClick={() => setShowRoleplay(true)}
                  className="bg-[#fe4a0b] hover:bg-[#fe4a0b]/90 text-white font-medium px-6"
                >
                  Start Practice Session
                </Button>
                <Button variant="outline" className="font-medium">
                  <Download className="w-4 h-4 mr-2" />
                  Download Brief
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>
          <Button variant="ghost" className="gap-2">
            Next
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SompoDemo;